import {Component, computed, inject, input, Signal, untracked} from '@angular/core';
import {FullTaDetails, ItineraryItemDetails, UserTravelItinerary} from '../../../../api';
import {TaAppStore} from '../../../../state/clcta-store';
import {TaSubmissionDatasource} from '../../../../datasources/ta-submission.datasource';
import {ItineraryDiffItemDetails} from '../../../../util/app.types';
import {CurrencyPipe, DatePipe, NgClass} from '@angular/common';
import {TableModule} from 'primeng/table';
import {Tooltip} from 'primeng/tooltip';
import {Checkbox} from 'primeng/checkbox';
import {FormsModule} from '@angular/forms';
import {itineraryDayRowClass, resolveItineraryTravellerId} from '../../../../util/general.utils';
import {hasOverseasValue, latestItinerariesForSubmission} from '../../../../util/itinerary.utils';

@Component({
  selector: 'itinerary-difference-comparison',
  imports: [
    CurrencyPipe,
    DatePipe,
    TableModule,
    NgClass,
    Tooltip,
    Checkbox,
    FormsModule
  ],
  templateUrl: './itinerary-difference-comparison.component.html',
  styleUrl: './itinerary-difference-comparison.component.scss'
})
export class ItineraryDifferenceComparisonComponent {
  store = inject(TaAppStore);
  taSubmissionDatasource = inject(TaSubmissionDatasource);
  protected readonly itineraryDayRowClass = itineraryDayRowClass;

  readonly taSubmission: Signal<FullTaDetails | undefined> = this.store.taSubmission
  readonly currentItinerary: Signal<UserTravelItinerary | undefined> = this.store.currentItinerary;
  readonly travellersLength = computed<number>(() => (this.taSubmission()?.travellers?.length ?? 0))
  // Off when the host renders its own per-traveller breakdown (day trip payments).
  showTravellerPortions = input<boolean>(true);

  userItineraries = computed<UserTravelItinerary[]>(() => {
    // Falls back past the primary traveller so an all-casual delegate TA - which has none - still
    // resolves to the traveller whose itinerary this table compares.
    let travellerId = resolveItineraryTravellerId(this.taSubmission(), this.store.user()?.id);
    let itineraries = this.taSubmissionDatasource.allItineraries() ?? [];
    return itineraries
      .filter(itin => itin.userTravelId === travellerId)
      .sort((a, b) => {
        if (a.version == null && b.version == null) { return 0; }
        if (a.version == null) { return 1; }
        if (b.version == null) { return -1; }
        return a.version - b.version;
      });
  });
  originalItineraryItems = computed<ItineraryItemDetails[] | undefined>(() => {
    if (this.userItineraries().length > 1 && !!this.userItineraries()[0].items) {
      return this.userItineraries()[0].items?.sort((a, b) => this.parseDate(a.start) - this.parseDate(b.start));
    }
    return undefined;
  });
  currentItineraryItems = computed<ItineraryItemDetails[]>(() => {
    let items = [];
    let length = this.userItineraries().length;
    if (length > 1) {
      items = this.userItineraries()[length-1].items ?? [];
    } else {
      items =  this.currentItinerary()?.items ?? [];
    }
    return items?.sort((a, b) => this.parseDate(a.start) - this.parseDate(b.start));
  });
  // Overseas cost column shows when any traveller on this TA has an overseas claim
  showOverseasColumn = computed<boolean>(() =>
    hasOverseasValue([...(this.originalItineraryItems() ?? []), ...this.currentItineraryItems()])
    || latestItinerariesForSubmission(this.taSubmissionDatasource.allItineraries(), this.taSubmission()?.id)
      .some(itinerary => hasOverseasValue(itinerary.items ?? [])));
  changeDetectionItems = computed<ItineraryDiffItemDetails[]>(() => {
    const { startDate, endDate } = untracked(() => this.taSubmission() ?? {});
    if (!startDate || !endDate) {
      return [];
    }
    if (this.userItineraries().length <= 1) {
      return [];
    }
    // Span every day with an item in either version, so days dropped by a date shift still show as deleted.
    // Starts are local wall-clock (no offset); local midnight matches the loop's day buckets.
    const itemDays = [...(this.originalItineraryItems() ?? []), ...this.currentItineraryItems()]
      .filter(item => !!item.start)
      .map(item => new Date(item.start!).setHours(0, 0, 0, 0));
    let start = itemDays.length ? new Date(Math.min(...itemDays)) : new Date(startDate);
    let end = itemDays.length ? new Date(Math.max(...itemDays)) : new Date(endDate);

    let diffItems: ItineraryDiffItemDetails[] = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayStart = new Date(d);
      const dayEnd = new Date(d);
      dayEnd.setDate(dayEnd.getDate() + 1);

      // helper to check if an item's start date falls in this day
      const isInDay = (elem: any) => {
        if (!elem?.start) return false;
        const elemDate = new Date(elem.start);
        return elemDate >= dayStart && elemDate < dayEnd;
      };

      const originalItem =
        this.originalItineraryItems()?.find(isInDay) ?? null;

      const currentItem =
        this.currentItineraryItems()?.find(isInDay) ?? null;

      if (!originalItem && !currentItem) {
        //do nothing
      } else if (!originalItem && currentItem) {
        let newDiffItem: ItineraryDiffItemDetails = { ...currentItem, fieldChanged: {}, dateChangedClass: 'background: var(--p-green-100);'}
        for (const key of this.ITINERARY_FIELDS) {
          newDiffItem.fieldChanged[key] = 'background: var(--p-green-100);';
        }
        diffItems.push(newDiffItem);
      } else if (originalItem && !currentItem) {
        let newDiffItem: ItineraryDiffItemDetails = { ...originalItem, fieldChanged: {}, dateChangedClass: 'background: var(--p-red-50); text-decoration: line-through;'}
        for (const key of this.ITINERARY_FIELDS) {
          newDiffItem.fieldChanged[key] = 'background: var(--p-red-50); text-decoration: line-through;';
        }
        diffItems.push(newDiffItem);
      } else if (originalItem && currentItem) {
        let newDiffItem: ItineraryDiffItemDetails = {...currentItem, fieldChanged: {}};
        for (const key of this.ITINERARY_FIELDS) {
          const oldVal = originalItem[key]?.toString() ?? '';
          const newVal = currentItem[key]?.toString() ?? '';

          if (oldVal !== newVal) {
            if (!!newDiffItem.fieldChanged) {
              newDiffItem.fieldChanged[key] = 'border: 1px solid var(--color-yellow-500);';
            }
          }
        }
        diffItems.push(newDiffItem);
      }
    }
    return diffItems;
  });
  readonly ITINERARY_FIELDS = [
    'startLocationNameId',
    'endLocationNameId',
    'start',
    'end',
    'workType',
    'cateringOption',
    'travelMode',
    'accommodationType',
    'supportingInfo',
    'rateCategory',
    'breakfastValue',
    'lunchValue',
    'dinnerValue',
    'incidentalValue',
    'cateringValue',
    'overseasValue',
    'dayTripValue',
    'isPersonalDay'
  ] as const satisfies readonly (keyof ItineraryItemDetails)[];

  parseDate = (d?: string | null) => d ? new Date(d).getTime() : Number.POSITIVE_INFINITY;
}
